import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { Button, Card, PageHeaderLayout, Typography } from '@/ui';

import type { TypeformAdminFormFieldGroup } from '@/store/api/typeformAdminApi';

import {
  useBackfillTypeformAdminFormMutation,
  useGetTypeformAdminFormGroupsQuery,
  useGetTypeformAdminFormMappingsQuery,
  useSetTypeformAdminFormGroupsMutation,
  useSetTypeformAdminFormMappingsMutation,
  useSyncTypeformAdminFormDefinitionMutation,
} from '@/store/api/typeformAdminApi';

type MappingPurpose = 'company' | 'email' | 'first_name' | 'last_name';

const mappingPurposes: Array<{ key: MappingPurpose; label: string }> = [
  { key: 'email', label: 'Email' },
  { key: 'first_name', label: 'First name' },
  { key: 'last_name', label: 'Last name' },
  { key: 'company', label: 'Company' },
];

interface GroupDraft {
  canonicalRef: string;
  fieldGroup: string;
}

const TypeformFormMappingsPage = () => {
  const { formId = '' } = useParams();
  const [notice, setNotice] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [mappingDrafts, setMappingDrafts] = useState<
    Record<MappingPurpose, string>
  >({
    company: '',
    email: '',
    first_name: '',
    last_name: '',
  });
  const [groupDrafts, setGroupDrafts] = useState<Record<string, GroupDraft>>(
    {},
  );
  const { data: mappingsData, isLoading: isMappingsLoading } =
    useGetTypeformAdminFormMappingsQuery(formId);
  const { data: groupsData, isLoading: isGroupsLoading } =
    useGetTypeformAdminFormGroupsQuery(formId);
  const [saveMappings, { isLoading: isSavingMappings }] =
    useSetTypeformAdminFormMappingsMutation();
  const [saveGroups, { isLoading: isSavingGroups }] =
    useSetTypeformAdminFormGroupsMutation();
  const [syncDefinition, { isLoading: isSyncingDefinition }] =
    useSyncTypeformAdminFormDefinitionMutation();
  const [backfillForm, { isLoading: isBackfilling }] =
    useBackfillTypeformAdminFormMutation();
  const [hasAttemptedDefinitionSync, setHasAttemptedDefinitionSync] =
    useState(false);

  const availableFields = useMemo(
    () => mappingsData?.availableFields ?? groupsData?.availableFields ?? [],
    [groupsData?.availableFields, mappingsData?.availableFields],
  );

  useEffect(() => {
    if (!mappingsData) return;

    setMappingDrafts({
      company:
        mappingsData.mappings.find(
          (mapping) => mapping.fieldPurpose === 'company',
        )?.typeformFieldRef ?? '',
      email:
        mappingsData.mappings.find(
          (mapping) => mapping.fieldPurpose === 'email',
        )?.typeformFieldRef ?? '',
      first_name:
        mappingsData.mappings.find(
          (mapping) => mapping.fieldPurpose === 'first_name',
        )?.typeformFieldRef ?? '',
      last_name:
        mappingsData.mappings.find(
          (mapping) => mapping.fieldPurpose === 'last_name',
        )?.typeformFieldRef ?? '',
    });
  }, [mappingsData]);

  useEffect(() => {
    if (!groupsData) return;

    setGroupDrafts(
      groupsData.groups.reduce<Record<string, GroupDraft>>((acc, group) => {
        acc[group.typeformFieldRef] = {
          canonicalRef: group.canonicalRef ?? '',
          fieldGroup: group.fieldGroup,
        };
        return acc;
      }, {}),
    );
  }, [groupsData]);

  useEffect(() => {
    if (
      !formId ||
      hasAttemptedDefinitionSync ||
      isMappingsLoading ||
      isGroupsLoading ||
      !mappingsData ||
      !groupsData ||
      availableFields.length > 0
    ) {
      return undefined;
    }

    let isMounted = true;
    setHasAttemptedDefinitionSync(true);
    setErrorMessage('');
    setNotice('Syncing Typeform definition to load field options…');

    const syncMissingDefinition = async () => {
      try {
        await syncDefinition(formId).unwrap();
        if (!isMounted) return;
        setNotice('Definition synced. Field options reloaded.');
      } catch (error) {
        console.error(error);
        if (!isMounted) return;
        setNotice('');
        setErrorMessage(
          'No cached definition is loaded for this form. Sync the definition to load field options.',
        );
      }
    };

    syncMissingDefinition();

    return () => {
      isMounted = false;
    };
  }, [
    availableFields.length,
    formId,
    groupsData,
    hasAttemptedDefinitionSync,
    isGroupsLoading,
    isMappingsLoading,
    mappingsData,
    syncDefinition,
  ]);

  if (isMappingsLoading || isGroupsLoading || !mappingsData || !groupsData) {
    return (
      <div className="container mx-auto max-w-screen-xl p-4">
        <Typography variant="body1">Loading form configuration…</Typography>
      </div>
    );
  }

  const handleSaveMappings = async () => {
    try {
      setErrorMessage('');
      const mappings = mappingPurposes
        .map(({ key }) => {
          const fieldRef = mappingDrafts[key];
          if (!fieldRef) return null;
          const field = availableFields.find((item) => item.ref === fieldRef);
          return {
            fieldPurpose: key,
            fieldTitle: field?.title ?? undefined,
            typeformFieldId: field?.id ?? undefined,
            typeformFieldRef: fieldRef,
          };
        })
        .filter(Boolean);

      if (mappings.length === 0) {
        setErrorMessage('Select at least one mapping before saving.');
        return;
      }

      await saveMappings({
        formId,
        mappings: mappings as NonNullable<(typeof mappings)[number]>[],
      }).unwrap();
      setNotice('Mappings saved.');
    } catch (error) {
      console.error(error);
      setErrorMessage('Could not save mappings.');
    }
  };

  const handleSaveGroups = async () => {
    try {
      setErrorMessage('');
      const groups = Object.entries(groupDrafts)
        .filter(([, group]) => group.fieldGroup)
        .map(([typeformFieldRef, group]) => {
          const field = availableFields.find(
            (item) => item.ref === typeformFieldRef,
          );
          return {
            canonicalRef: group.canonicalRef || null,
            fieldGroup:
              group.fieldGroup as TypeformAdminFormFieldGroup['fieldGroup'],
            fieldTitle: field?.title ?? undefined,
            typeformFieldRef,
          };
        });

      await saveGroups({ formId, groups }).unwrap();
      setNotice('Field groups saved.');
    } catch (error) {
      console.error(error);
      setErrorMessage('Could not save field groups.');
    }
  };

  const handleBackfill = async () => {
    try {
      setErrorMessage('');
      const result = await backfillForm(formId).unwrap();
      setNotice(
        `Backfill finished: ${result.updated} updated, ${result.failed} failed.`,
      );
    } catch (error) {
      console.error(error);
      setErrorMessage('Could not backfill this form.');
    }
  };

  const handleSyncDefinition = async () => {
    try {
      setErrorMessage('');
      setNotice('');
      setHasAttemptedDefinitionSync(true);
      await syncDefinition(formId).unwrap();
      setNotice('Definition synced. Field options reloaded.');
    } catch (error) {
      console.error(error);
      setErrorMessage('Could not sync the Typeform definition for this form.');
    }
  };

  return (
    <div className="container mx-auto max-w-screen-xl p-4">
      <PageHeaderLayout
        subtitle={mappingsData.formType ?? 'Unknown form type'}
        title="Typeform Interpretation"
      >
        <Link className="no-underline" to="/coach/manage/typeform?tab=forms">
          <Button variant="outlined">Back to forms</Button>
        </Link>
      </PageHeaderLayout>

      {notice ? (
        <div className="mt-4 rounded border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
          {notice}
        </div>
      ) : null}
      {errorMessage ? (
        <div className="mt-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {errorMessage}
        </div>
      ) : null}
      {availableFields.length === 0 ? (
        <Card className="mt-4 p-4">
          <Typography variant="h5">Field options unavailable</Typography>
          <Typography className="mt-1" variant="body2">
            This form does not have a cached Typeform definition yet. Sync the
            definition to load field ids, refs, and titles for mappings and
            groups.
          </Typography>
          <div className="mt-4">
            <Button
              disabled={isSyncingDefinition}
              variant="outlined"
              onClick={() => {
                handleSyncDefinition();
              }}
            >
              Sync definition
            </Button>
          </div>
        </Card>
      ) : null}

      <div className="mt-6 grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Card className="p-4">
          <Typography variant="h5">Identity mappings</Typography>
          <Typography className="mt-1" variant="body2">
            Choose the field refs used to extract respondent identity and run a
            backfill after changes.
          </Typography>

          <div className="mt-4 flex flex-col gap-3">
            {mappingPurposes.map((purpose) => (
              <div key={purpose.key}>
                <Typography variant="body2">{purpose.label}</Typography>
                <select
                  aria-label={purpose.label}
                  className="mt-1 w-full rounded border border-sage-300 p-2"
                  id={`mapping-${purpose.key}`}
                  value={mappingDrafts[purpose.key]}
                  onChange={(event) =>
                    setMappingDrafts((current) => ({
                      ...current,
                      [purpose.key]: event.target.value,
                    }))
                  }
                >
                  <option value="">Unmapped</option>
                  {availableFields.map((field) => (
                    <option key={field.ref} value={field.ref ?? ''}>
                      {field.title ?? field.ref} ({field.ref})
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <Button
              disabled={isSavingMappings}
              onClick={() => {
                handleSaveMappings();
              }}
            >
              Save mappings
            </Button>
            <Button
              disabled={isBackfilling}
              variant="outlined"
              onClick={() => {
                handleBackfill();
              }}
            >
              Run backfill
            </Button>
          </div>
        </Card>

        <Card className="p-4">
          <Typography variant="h5">Field groups</Typography>
          <Typography className="mt-1" variant="body2">
            Tag each available field with a logical group and optional canonical
            reference.
          </Typography>

          <div className="mt-4 flex flex-col gap-3">
            {availableFields.length ? (
              availableFields.map((field) => {
                const draft = groupDrafts[field.ref ?? ''] ?? {
                  canonicalRef: '',
                  fieldGroup: '',
                };

                return (
                  <div
                    key={field.ref}
                    className="rounded border border-sage-200 p-3"
                  >
                    <div className="flex flex-col gap-1">
                      <Typography variant="subtitle2">
                        {field.title ?? field.ref}
                      </Typography>
                      <Typography variant="body2">
                        {field.ref} {field.type ? `• ${field.type}` : ''}
                      </Typography>
                    </div>
                    <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div>
                        <Typography variant="body2">Group</Typography>
                        <select
                          aria-label="Group"
                          className="mt-1 w-full rounded border border-sage-300 p-2"
                          id={`group-${field.ref}`}
                          value={draft.fieldGroup}
                          onChange={(event) =>
                            setGroupDrafts((current) => ({
                              ...current,
                              [field.ref ?? '']: {
                                canonicalRef: draft.canonicalRef,
                                fieldGroup: event.target.value,
                              },
                            }))
                          }
                        >
                          <option value="">Unassigned</option>
                          {groupsData.validGroups.map((group) => (
                            <option key={group} value={group}>
                              {group}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Typography variant="body2">Canonical ref</Typography>
                        <input
                          aria-label="Canonical ref"
                          className="mt-1 w-full rounded border border-sage-300 p-3"
                          id={`canonical-ref-${field.ref}`}
                          list={`canonical-refs-${field.ref}`}
                          value={draft.canonicalRef}
                          onChange={(event) =>
                            setGroupDrafts((current) => ({
                              ...current,
                              [field.ref ?? '']: {
                                canonicalRef: event.target.value,
                                fieldGroup: draft.fieldGroup,
                              },
                            }))
                          }
                        />
                      </div>
                      <datalist id={`canonical-refs-${field.ref}`}>
                        {groupsData.canonicalRefs.map((canonicalRef) => (
                          <option key={canonicalRef} value={canonicalRef} />
                        ))}
                      </datalist>
                    </div>
                  </div>
                );
              })
            ) : (
              <Typography variant="body2">
                No available fields were found for this form.
              </Typography>
            )}
          </div>

          <div className="mt-4">
            <Button
              disabled={isSavingGroups}
              onClick={() => {
                handleSaveGroups();
              }}
            >
              Save field groups
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default TypeformFormMappingsPage;
